import { roundToNextQuarterHour } from '@gr/portal/providers/utility.provider';
import { CampaignStatusEnum, ProviderSwapValidationCodeEnum, UiCampaignStatusEnum } from '@gr/shared/enums';
import {
  FilterDataTypeEnum,
  FilterOperatorEnum,
  IHttpResponse,
  IProvidersChangeForTypeRequest,
  IProvidersChangeForTypeValidationResponse,
  ISearchFilter,
  ISendingWindow
} from '@gr/shared/models';
import { isInSendingWindow } from '@gr/shared/time';
import { addDays, subMinutes } from 'date-fns';
import { DateTime } from 'luxon';
import { axiosPost } from '../../../authAxios';

export const getCampaignStartDate = (): Date => {
  const startDate = roundToNextQuarterHour(new Date());

  return startDate;
};

export const getCampaignEndDateForStartDate = (startDate: Date) => {


  const startDateEst = DateTime.fromJSDate(startDate, { zone: 'America/New_York' });
  const est = DateTime.local({ zone: 'America/New_York' });
  const estOffset = Math.abs(est.offset / 60);
  const localOffset = startDate.getTimezoneOffset() / 60;

  const date = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDateEst.day + 1, localOffset - (localOffset - estOffset), 0));

  // To skip confusion over midnight
  if (date.getHours() === 0) {
    return subMinutes(date, 1);
  }

  return date;
};

export const getCampaignEndDateForClone = (startDate: Date, endDate: Date) => {

  if (startDate > endDate) {
    return getCampaignEndDateForStartDate(startDate);
  } else {
    return endDate;
  }

};

export function isMoreThanTwoDays(startDate: Date, endDate: Date): boolean {
  return endDate > getCampaignEndDateForStartDate(addDays(startDate, 2));
}

export interface GetUIStatusRequest {
  status: CampaignStatusEnum;
  startsAt: Date;
  totalMessagesConfirmed: number;
  totalMessagesScheduled: number;
  totalMessagesSent: number;
  sendingWindow: ISendingWindow;
  allListsClassified: boolean;
}

export const mapToUiStatus = (request: GetUIStatusRequest): UiCampaignStatusEnum => {
  switch (request.status) {
    case CampaignStatusEnum.LIVE:
      return getLiveUiStatus(request);
    case CampaignStatusEnum.COMPLETED:
      return request.totalMessagesScheduled === request.totalMessagesSent ? UiCampaignStatusEnum.COMPLETED : UiCampaignStatusEnum.INCOMPLETE;
    case CampaignStatusEnum.FAILED:
      return UiCampaignStatusEnum.DID_NOT_SCHED;
    case CampaignStatusEnum.SCHEDULING:
      return !request.allListsClassified ? UiCampaignStatusEnum.PENDING : UiCampaignStatusEnum.SCHEDULING;
    case CampaignStatusEnum.PAUSED:
      return UiCampaignStatusEnum.PAUSED;
    default:
      return UiCampaignStatusEnum.DRAFT;
  }
};

export function getLiveUiStatus(request: GetUIStatusRequest) {
  const now = new Date();
  const startsAt = new Date(request.startsAt!.toString());

  if (startsAt > now) {
    return UiCampaignStatusEnum.SCHEDULED;
  } else if (!isInSendingWindow(request.sendingWindow)) {
    return UiCampaignStatusEnum.QUEUED;
  } else {
    return request.totalMessagesConfirmed === 0
      ? UiCampaignStatusEnum.ACTIVE
      : UiCampaignStatusEnum.SENDING;
  }
}

/**
 * Resolves a user-facing message for a failed provider swap.
 *
 * The swap endpoint returns a 400 with an explanatory body when the new provider has no phone
 * numbers in an active campaign's preferred area code — that message is the whole point of the
 * guard, so it must reach the admin verbatim. Anything 5xx or transport-level falls back to a
 * generic string so raw Axios text ("Request failed with status code 500") is never surfaced.
 */
export const getProviderSwapErrorContent = (error: any): string => {
  const status = error?.response?.status;

  if (status === 401 || status === 403) {
    return 'You are not authorized to change the provider for this campaign type.';
  }

  const message = error?.response?.data?.message;
  if (message && status < 500) {
    return message;
  }

  return 'An unexpected error occurred when attempting to change the provider.';
};

/**
 * Pre-flights a provider swap through the area-code guard without writing anything
 * (validateOnly, MES-2587). A blocked swap comes back as a 200 with the block reason in `code`,
 * so the caller can branch: NO_AREA_CODE_COVERAGE is confirmable, everything else fails closed.
 * Transport/auth failures reject and are expected to flow into the caller's existing catch.
 */
export const validateProviderSwap = async (
  request: IProvidersChangeForTypeRequest
): Promise<IProvidersChangeForTypeValidationResponse | undefined> => {
  const response = await axiosPost<IHttpResponse<IProvidersChangeForTypeValidationResponse>>(
    '/providers-change-for-type',
    { ...request, validateOnly: true }
  );

  return response?.data?.data;
};

/**
 * Maps a non-confirmable validateOnly block code to a user-facing message. The validateOnly 200
 * body carries no message text, so the frontend owns this mapping — kept in one place so every
 * swap entry point resolves codes identically and a future third code lands here once.
 * NO_AREA_CODE_COVERAGE never reaches this function (it opens the confirm modal instead); any
 * unrecognized code fails closed with a generic message, mirroring the backend's default branch.
 */
export const getProviderSwapValidationErrorContent = (code?: ProviderSwapValidationCodeEnum): string =>
  code === ProviderSwapValidationCodeEnum.PROVIDER_NOT_FOUND
    ? 'The selected provider could not be found.'
    : 'The provider change could not be validated.';

export enum ProviderSwapOutcomeEnum {
  COMMITTED = 'COMMITTED',
  CANCELLED = 'CANCELLED',
  BLOCKED = 'BLOCKED',
}

export interface IProviderSwapOutcome {
  outcome: ProviderSwapOutcomeEnum;
  // User-facing reason, set only when BLOCKED.
  blockedContent?: string;
}

/**
 * Pre-flight → confirm → commit for a manual provider swap (MES-2614, on the MES-2587 contract).
 * Phase 1 runs the area-code guard with zero writes; a non-confirmable block (PROVIDER_NOT_FOUND
 * or any future code) fails closed as BLOCKED. Phase 2 asks the caller's confirm gate only for
 * NO_AREA_CODE_COVERAGE; a decline returns CANCELLED with nothing written. Phase 3 commits, sending
 * the override flag only after the user confirmed. Transport errors propagate to the caller, which
 * owns the "provider not changed" messaging.
 */
export const executeProviderSwap = async (
  request: IProvidersChangeForTypeRequest,
  confirmAreaCodeOverride: () => Promise<boolean>
): Promise<IProviderSwapOutcome> => {
  const validation = await validateProviderSwap(request);
  const requiresOverride = !!validation && !validation.isValid;

  if (requiresOverride && validation?.code !== ProviderSwapValidationCodeEnum.NO_AREA_CODE_COVERAGE) {
    return {
      outcome: ProviderSwapOutcomeEnum.BLOCKED,
      blockedContent: getProviderSwapValidationErrorContent(validation?.code),
    };
  }

  if (requiresOverride && !(await confirmAreaCodeOverride())) {
    return { outcome: ProviderSwapOutcomeEnum.CANCELLED };
  }

  await axiosPost<IHttpResponse<void>>(
    '/providers-change-for-type',
    requiresOverride ? { ...request, overrideAreaCodeWarning: true } : request
  );

  return { outcome: ProviderSwapOutcomeEnum.COMMITTED };
};

export type CampaignStatusFilter = {
  value: CampaignStatusEnum;
  additionalFilters?: ISearchFilter[];
};

export const mapToStatusFilter = (status: UiCampaignStatusEnum, sendingWindow: ISendingWindow | undefined): CampaignStatusFilter => {
  // This filter always returns an empty result
  const EMPTY_RESULTS_FILTER: ISearchFilter = {
    fieldName: 'createdAt',
    dataType: FilterDataTypeEnum.DATE,
    operator: FilterOperatorEnum.NOT_EQUALS,
    value: 'createdAt',
    isValueField: true
  };

  const inSendingWindow = sendingWindow && isInSendingWindow(sendingWindow);

  switch (status) {
    case UiCampaignStatusEnum.SCHEDULED: {
      const additionalFilters: ISearchFilter[] = [{
        fieldName: 'startsAt',
        dataType: FilterDataTypeEnum.DATE,
        operator: FilterOperatorEnum.GREATER_THAN,
        value: new Date()
      }];

      return {
        value: CampaignStatusEnum.LIVE,
        additionalFilters
      };
    }

    case UiCampaignStatusEnum.QUEUED: {
      const additionalFilters: ISearchFilter[] = [{
        fieldName: 'startsAt',
        dataType: FilterDataTypeEnum.DATE,
        operator: FilterOperatorEnum.LESS_THAN_OR_EQUALS,
        value: new Date()
      }];

      if (inSendingWindow) {
        additionalFilters.push(EMPTY_RESULTS_FILTER);
      }

      return {
        value: CampaignStatusEnum.LIVE,
        additionalFilters
      };
    }

    case UiCampaignStatusEnum.ACTIVE:
      const additionalFilters: ISearchFilter[] = [{
        fieldName: 'startsAt',
        dataType: FilterDataTypeEnum.DATE,
        operator: FilterOperatorEnum.LESS_THAN_OR_EQUALS,
        value: new Date()
      }, {
        fieldName: 'totalMessagesConfirmed',
        dataType: FilterDataTypeEnum.NUMBER,
        operator: FilterOperatorEnum.EQUALS,
        value: 0
      }];

      if (!inSendingWindow) {
        additionalFilters.push(EMPTY_RESULTS_FILTER);
      }

      return {
        value: CampaignStatusEnum.LIVE,
        additionalFilters
      };

    case UiCampaignStatusEnum.SENDING: {
      const additionalFilters: ISearchFilter[] = [{
        fieldName: 'startsAt',
        dataType: FilterDataTypeEnum.DATE,
        operator: FilterOperatorEnum.LESS_THAN_OR_EQUALS,
        value: new Date()
      }, {
        fieldName: 'totalMessagesConfirmed',
        dataType: FilterDataTypeEnum.NUMBER,
        operator: FilterOperatorEnum.NOT_EQUALS,
        value: 0
      }];

      if (!inSendingWindow) {
        additionalFilters.push(EMPTY_RESULTS_FILTER);
      }

      return {
        value: CampaignStatusEnum.LIVE,
        additionalFilters
      };
    }

    case UiCampaignStatusEnum.COMPLETED:
      return {
        value: CampaignStatusEnum.COMPLETED,
        additionalFilters: [{
          fieldName: 'totalMessagesScheduled',
          dataType: FilterDataTypeEnum.NUMBER,
          operator: FilterOperatorEnum.EQUALS,
          value: 'totalMessagesSent',
          isValueField: true
        }]
      };

    case UiCampaignStatusEnum.INCOMPLETE:
      return {
        value: CampaignStatusEnum.COMPLETED,
        additionalFilters: [{
          fieldName: 'totalMessagesScheduled',
          dataType: FilterDataTypeEnum.NUMBER,
          operator: FilterOperatorEnum.NOT_EQUALS,
          value: 'totalMessagesSent',
          isValueField: true
        }]
      };

    case UiCampaignStatusEnum.PENDING:
      return {
        value: CampaignStatusEnum.SCHEDULING,
        additionalFilters: [{
          fieldName: 'allListsClassified',
          dataType: FilterDataTypeEnum.BOOLEAN,
          operator: FilterOperatorEnum.EQUALS,
          value: false
        }]
      };

    case UiCampaignStatusEnum.DID_NOT_SCHED:
      return { value: CampaignStatusEnum.FAILED };

    case UiCampaignStatusEnum.DRAFT:
      return { value: CampaignStatusEnum.DRAFT };

    case UiCampaignStatusEnum.PAUSED:
      return { value: CampaignStatusEnum.PAUSED };

    case UiCampaignStatusEnum.SCHEDULING:
      return { value: CampaignStatusEnum.SCHEDULING };

    default:
      return { value: CampaignStatusEnum.DRAFT };
  }
};
